import axios from "axios";
import {
    getClientId,
    getExpirationDate,
    getStarfleetBase,
    getVoltronBase,
    isCNTLD,
} from "../../common/utils";
import i18n from "../../i18n";
import * as actionTypes from "./actionTypes";
import { setLoading } from "./loading";
import { setBarMessage } from "./message";

export const logout = (dispatch) => {
    localStorage.removeItem("prvyFedarationData");
    localStorage.removeItem("prvySessionData");
    localStorage.removeItem("PrvyConsentData");
    dispatch({
        type: actionTypes.AUTH_LOGOUT,
    });
};

export const authorize = (pathname, locale, isAccount) => {
    let redirect_uri = isAccount
        ? `${location.href.split(pathname)[0]}/account/email-preferences/`
        : `${location.href.split(pathname)[0]}/preferences/verify-email/`;
    const [lang, region] = locale.split("-");
    const searchParams = new URLSearchParams({
        redirect_uri,
        client_id: getClientId(isAccount),
        ui_locales: `${lang}-${region.toUpperCase()}`,
    });
    window.location.href = `${AUTH_URL}?${searchParams.toString()}`;
};

export const sendUpdateExtendedUserProfileRequest = async (profile, idToken) => {
    const res = await axios.post(`${getVoltronBase()}${EUP_BASE}/v1/user/updateProfile`, profile, {
        headers: { idtoken: idToken },
    });
    return res;
};

export const updateExtendedUserProfileRequest = (idToken) => {
    return {
        type: actionTypes.UPDATE_EXTENDED_USER_PROFILE_REQUEST,
        payload: {
            idToken,
        },
    };
};

export const updateExtendedUserProfileSuccess = (profile) => {
    return {
        type: actionTypes.UPDATE_EXTENDED_USER_PROFILE_SUCCESS,
        payload: {
            profile,
        },
    };
};

export const updateExtendedUserProfileFailure = (error) => {
    return {
        type: actionTypes.UPDATE_EXTENDED_USER_PROFILE_FAILURE,
        payload: { error },
    };
};

export const updateExtendedUserProfile = async (dispatch, idToken) => {
    dispatch(updateExtendedUserProfileRequest(idToken));
    dispatch(setLoading(true, "updateEUP"));
    try {
        const res = await sendUpdateExtendedUserProfileRequest(accessToken);
        dispatch(updateExtendedUserProfileSuccess(res));
        dispatch(setLoading(false, "updateEUP"));
    } catch (error) {
        dispatch(updateExtendedUserProfileFailure(error));
        dispatch(setLoading(false, "updateEUP"));
        dispatch(
            setBarMessage({
                type: "Error",
                content: i18n.t("Something went wrong, please try again."),
            })
        );
    }
};

export const sendGetExtendedUserProfileRequest = async (idToken) => {
    const res = await axios.get(`${getVoltronBase()}${EUP_BASE}/v1/user/getProfile`, {
        headers: { idtoken: idToken },
    });
    return res;
};

export const getExtendedUserProfileRequest = (idToken) => {
    return {
        type: actionTypes.GET_EXTENDED_USER_PROFILE_REQUEST,
        payload: {
            idToken,
        },
    };
};

export const getExtendedUserProfileSuccess = (response) => {
    return {
        type: actionTypes.GET_EXTENDED_USER_PROFILE_SUCCESS,
        payload: {
            response: response.data,
        },
    };
};

export const getExtendedUserProfileFailure = (error) => {
    return {
        type: actionTypes.GET_EXTENDED_USER_PROFILE_FAILURE,
        payload: { error },
    };
};

export const getExtendedUserProfile = async (dispatch, idToken) => {
    dispatch(getExtendedUserProfileRequest(idToken));
    dispatch(setLoading(true, "getEUP"));
    try {
        const res = await sendGetExtendedUserProfileRequest(idToken);
        dispatch(getExtendedUserProfileSuccess(res));
        dispatch(setLoading(false, "getEUP"));
    } catch (error) {
        dispatch(getExtendedUserProfileFailure(error));
        dispatch(setLoading(false, "getEUP"));
        dispatch(
            setBarMessage({
                type: "Error",
                content: i18n.t("Something went wrong, please try again."),
            })
        );
    }
};

export const sendGetProfileRequest = async (accessToken) => {
    const res = await axios.get(`${getStarfleetBase()}${PROFILE_ENDPOINT}`, {
        params: {},
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    return res;
};

export const getProfileRequest = (accessToken, idToken) => {
    return {
        type: actionTypes.GET_PROFILE_REQUEST,
        payload: {
            accessToken,
            idToken,
        },
    };
};

export const getProfileSuccess = ({ email, matchVersion }) => {
    return {
        type: actionTypes.GET_PROFILE_SUCCESS,
        payload: {
            email,
            matchVersion,
        },
    };
};

export const getProfileFailure = (error) => {
    return {
        type: actionTypes.GET_PROFILE_FAILURE,
        payload: { error },
    };
};

export const getProfile = async (dispatch, accessToken, idToken) => {
    dispatch(getProfileRequest(accessToken, idToken));
    dispatch(setLoading(true, "getProfile"));
    try {
        const res = await sendGetProfileRequest(accessToken);
        const email = res.data.emails[0].value;
        const matchVersion = res.data.meta.version;
        dispatch(getProfileSuccess({ email, matchVersion }));
        dispatch(setLoading(false, "getProfile"));
    } catch (error) {
        dispatch(getProfileFailure(error));
        dispatch(setLoading(false, "getProfile"));
        dispatch(
            setBarMessage({
                type: "Error",
                content: i18n.t("Something went wrong, please try again."),
            })
        );
    }
};

export const sendGetPINRequest = async (email, locale, accessToken, idToken) => {
    let headers = {};
    if (accessToken && idToken) {
        headers = {
            accesstoken: accessToken,
            idtoken: idToken,
        };
    }
    const res = await axios.get(`${getVoltronBase()}${PIN_ENDPOINT}`, {
        headers,
        params: {
            email,
            locale,
        },
    });
    return res;
};

export const getPINRequest = (email, locale) => {
    return {
        type: actionTypes.GET_PIN_REQUEST,
        payload: {
            email,
            locale,
        },
    };
};

export const getPINSuccess = (response, sessionExpiration) => {
    return {
        type: actionTypes.GET_PIN_SUCCESS,
        payload: {
            response: response.data,
            sessionExpiration,
        },
    };
};

export const getPINFailure = (error) => {
    return {
        type: actionTypes.GET_PIN_FAILURE,
        payload: { error },
    };
};

export const getPIN = async (dispatch, email, locale, accessToken, idToken, history, resend) => {
    dispatch(getPINRequest(email, locale));
    dispatch(setLoading(true, "getPIN"));
    try {
        const res = await sendGetPINRequest(email, locale, accessToken, idToken);
        const sessionExpiration = getExpirationDate(res.data.expiresIn);
        localStorage.setItem(
            "prvySessionData",
            JSON.stringify({
                email,
                sessionId: res.data.sessionId,
                sessionExpiration,
                valid: false,
            })
        );
        dispatch(getPINSuccess(res, sessionExpiration));
        dispatch(setLoading(false, "getPIN"));
        if (resend) {
            /* handling the case when the user is resending his pin
             * success message needs to be displayed
             */
            dispatch(
                setBarMessage({
                    type: "Success",
                    content: i18n.t("We have sent a new pin to {{email}}.", { email }),
                })
            );
        }
        if (history) {
            history.push("/preferences/verify-email/");
        }
    } catch (error) {
        dispatch(getPINFailure(error));
        dispatch(setLoading(false, "getPIN"));
        if (error.response && error.response.data.code === 1006) {
            dispatch(
                setBarMessage({
                    type: "Error",
                    content: i18n.t("Reached maximum limit of 5 attempts in 15 minutes."),
                })
            );
        } else {
            dispatch(
                setBarMessage({
                    type: "Error",
                    content: i18n.t("Something went wrong, please try again."),
                })
            );
        }
    }
};

export const sendVerifyPINRequest = async (pin, sessionId) => {
    const res = await axios.post(`${getVoltronBase()}${PIN_ENDPOINT}`, {
        pin,
        sessionId,
    });
    return res;
};

export const verifyPINRequest = (pin, sessionId) => {
    return {
        type: actionTypes.VERIFY_PIN_REQUEST,
        payload: {
            pin,
            sessionId,
        },
    };
};

export const verifyPINSuccess = (response, sessionExpiration) => {
    return {
        type: actionTypes.VERIFY_PIN_SUCCESS,
        payload: {
            response: response.data,
            sessionExpiration,
        },
    };
};

export const verifyPINFailure = (error) => {
    return {
        type: actionTypes.VERIFY_PIN_FAILURE,
        payload: { error },
    };
};

export const verifyPIN = async (dispatch, pin, sessionId, email, history) => {
    dispatch(verifyPINRequest(pin, sessionId));
    dispatch(setLoading(true, "verifyPIN"));
    try {
        const res = await sendVerifyPINRequest(pin, sessionId);
        const sessionExpiration = getExpirationDate(res.data.expiresIn);
        localStorage.setItem(
            "prvySessionData",
            JSON.stringify({
                email,
                sessionId: res.data.sessionId,
                sessionExpiration,
                valid: true,
            })
        );
        dispatch(setLoading(false, "verifyPIN"));
        dispatch(verifyPINSuccess(res, sessionExpiration));
        history.push("/preferences/email-preferences/");
    } catch (error) {
        dispatch(verifyPINFailure(error));
        dispatch(setLoading(false, "verifyPIN"));
        if (error.response && error.response.data.code === 1007) {
            dispatch(
                setBarMessage({
                    type: "Error",
                    content: i18n.t(
                        "This pin is invalid and may be expired. Select 'Resend Email' for a new pin."
                    ),
                })
            );
        } else if (error.response && error.response.data.code === 1008) {
            dispatch(
                setBarMessage({
                    type: "Error",
                    content: i18n.t(
                        "Confirmation email link is expired/invalid. Please verify your email address again below to access your preference center."
                    ),
                })
            );
            logout(dispatch);
            history.push("/preferences/start/");
        } else {
            dispatch(
                setBarMessage({
                    type: "Error",
                    content: i18n.t("Something went wrong, please try again."),
                })
            );
        }
    }
};

export const sendGetUserGeoInfo = async () => {
    if (sessionStorage.getItem(GEO_INFO_ITEM_NAME)) {
        return JSON.parse(sessionStorage.getItem(GEO_INFO_ITEM_NAME));
    }
    const { data } = await axios.get(`${GEO_ENDPOINT}`);
    const countryCode = data?.gfnServiceInfo?.clientCountryCode;
    const userGeoInfo = { countryCode };
    sessionStorage.setItem(GEO_INFO_ITEM_NAME, JSON.stringify(userGeoInfo));
    return userGeoInfo;
};

export const getUserGeoInfoRequest = () => {
    return {
        type: actionTypes.GET_USER_GEO_INFO_REQUEST,
    };
};

export const getUserGeoInfoSuccess = (response) => {
    return {
        type: actionTypes.GET_USER_GEO_INFO_SUCCESS,
        payload: { geoInfo: response },
    };
};

export const getUserGeoInfoFailure = (error) => {
    return {
        type: actionTypes.GET_USER_GEO_INFO_FAILURE,
        payload: { error },
    };
};

export const getUserGeoInfo = async (dispatch) => {
    dispatch(getUserGeoInfoRequest());
    dispatch(setLoading(true, "geo"));
    try {
        let geoInfoResponse;
        if (isCNTLD()) {
            geoInfoResponse = {
                countryCode: "CN",
            };
        } else {
            geoInfoResponse = await sendGetUserGeoInfo();
        }
        dispatch(setLoading(false, "geo"));
        dispatch(getUserGeoInfoSuccess(geoInfoResponse));
    } catch (error) {
        dispatch(getUserGeoInfoFailure(error));
        dispatch(setLoading(false, "geo"));
        dispatch(
            setBarMessage({
                type: "Error",
                content: i18n.t("Something went wrong, please try again."),
            })
        );
    }
};
